/**
 * src/lib/undoSend.js — TICKET-855, the 30-second undo window for outbound email.
 *
 * CEO 2026-09-21: "a clear history trail, once email is sent, with recall function
 * as well." An email is a one-way door, and the CEO/PMO write them on a phone —
 * the recall that matters is the one BEFORE it leaves.
 *
 * The queue is deliberately MODULE-level, not component state: the composer closes
 * the instant you press Send (that is the point — the UI must feel sent), so a
 * pending send has to outlive the component that queued it.
 *
 * TICKET-858 (CEO 2026-09-21: "save temporary the email, so in case it crashes,
 * you can recall the email"). Closing the tab inside the window flushes (see the
 * listeners below), but a CRASH kills the timer with the text still only in
 * memory. So every queued email now also writes a snapshot to localStorage, and
 * `recoverPendingEmails()` at app start turns any snapshot that outlived its
 * session back into a composer draft.
 *
 * It is deliberately recovered as a DRAFT and never auto-sent: a crash can happen
 * AFTER the request left the browser, so re-sending it could duplicate a mail the
 * recipient already has. Handing the text back is always safe; re-sending is not.
 *
 * ponytail: the hold itself still lives in this browser tab — the snapshot makes
 * the CONTENT survivable, not the schedule. A server outbox
 * (lead_emails.scheduled_send_at + a dispatch cron) is the upgrade path, and it
 * costs a migration and a kill switch this does not.
 */

/** How long a queued email waits, in ms. Gmail's own undo tops out at 30s. */
export const UNDO_WINDOW_MS = 30000;

/** localStorage namespace for in-flight email snapshots. Scoped by user to prevent
 * cross-user leakage on shared browsers (TICKET-964).
 */
const PENDING_PREFIX = 'acn.pending-email.';

/** Current user id for scoping pending email keys. Set by AppShell on auth. */
let currentUserId = null;

/**
 * Set the current user id for scoping pending email keys.
 * Called by AppShell after successful login.
 */
export function setUserIdForPendingEmails(userId) {
  currentUserId = userId;
}

/**
 * Get the scoped key prefix for pending emails (includes user id).
 * @private
 */
function getPendingKeyPrefix() {
  return currentUserId ? `${PENDING_PREFIX}${currentUserId}.` : PENDING_PREFIX;
}

/**
 * Clear all pending emails for the current user.
 * Called on signOut to prevent a different user from seeing previous user's drafts.
 */
export function clearCurrentUserPendingEmails() {
  if (!currentUserId) return;
  const prefix = getPendingKeyPrefix();
  const keysToDelete = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keysToDelete.push(k);
    }
  } catch { return; }
  for (const k of keysToDelete) {
    try { localStorage.removeItem(k); } catch { /* best-effort */ }
  }
}

/** id -> { timer, fire } for every send still inside its window. */
const pending = new Map();
let nextId = 1;

/**
 * A snapshot is `{ draftKey, to, subject, body }`. `draftKey` is the localStorage
 * key the owning composer restores its draft from, so recovery lands the text
 * exactly where that composer already looks for it — no second convention.
 */
function writeSnapshot(id, snapshot) {
  if (!snapshot) return;
  try {
    const key = getPendingKeyPrefix() + id;
    localStorage.setItem(key, JSON.stringify({ ...snapshot, queuedAt: Date.now() }));
  } catch { /* private mode / blocked storage: the send still works */ }
}

/** TICKET-1366: is this pending email still live in THIS session? Its snapshot is gone
 *  once it was recovered or cleared. Blocked storage cannot tell -> treat as live. */
function snapshotLive(id) {
  try {
    return localStorage.getItem(getPendingKeyPrefix() + id) !== null;
  } catch { return true; } // spine-emit-ok: blocked storage, fall back to the pending map alone
}

function dropSnapshot(id) {
  try {
    const key = getPendingKeyPrefix() + id;
    localStorage.removeItem(key);
  } catch { /* see writeSnapshot */ }
}

/**
 * Put an email's text back in its composer's draft slot. Shape matches what the
 * composers already read (`{ to, subject, body }`); EmailEntry reads only two of
 * the three and ignores the rest, which is why one shape serves all of them.
 */
export function restoreToDraft(snapshot) {
  if (!snapshot?.draftKey) return false;
  try {
    localStorage.setItem(snapshot.draftKey, JSON.stringify({
      to: snapshot.to || '',
      subject: snapshot.subject || '',
      body: snapshot.body || '',
    }));
    return true;
  } catch { return false; }
}

/**
 * Read a composer's saved draft, or null. Tolerates the two shapes in the tree:
 * EmailEntry has stored `{ body, subject }` since TICKET-207, and recovery writes
 * `{ to, subject, body }` — a missing key is simply absent, never a crash.
 */
export function readDraft(draftKey) {
  if (!draftKey) return null;
  try {
    const raw = localStorage.getItem(draftKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
}

/** Forget a composer's saved draft (called once the email has actually gone). */
export function clearDraft(draftKey) {
  if (!draftKey) return;
  try { localStorage.removeItem(draftKey); } catch { /* see writeSnapshot */ }
}

/**
 * Emails whose session ended before they resolved — i.e. the tab was killed
 * mid-window. Each one is restored to its composer draft and its snapshot
 * dropped, so this is safe to call more than once and never loops.
 *
 * Called once at app start (AppShell). Returns what was recovered so the caller
 * can say so out loud: silence here would be the same defect this ticket exists
 * to kill.
 *
 * @returns {Array<{ to:string, subject:string, draftKey:string, queuedAt:number }>}
 */
export function recoverPendingEmails() {
  const out = [];
  // TICKET-964: with no user known, the bare prefix would match EVERY user's scoped
  // keys -- recover nothing until the user id is set.
  if (!currentUserId) return out;
  const prefix = getPendingKeyPrefix();
  let keys = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
  } catch { return out; }

  for (const k of keys) {
    let snap = null;
    try { snap = JSON.parse(localStorage.getItem(k) || 'null'); } catch { snap = null; }
    try { localStorage.removeItem(k); } catch { /* best-effort */ }
    if (!snap || !snap.draftKey) continue;       // unusable snapshot: drop it, don't crash
    if (!restoreToDraft(snap)) continue;
    out.push({
      to: snap.to || '', subject: snap.subject || '',
      draftKey: snap.draftKey, queuedAt: snap.queuedAt || 0,
    });
  }
  return out;
}

/**
 * Queue `run` to execute after `delayMs` unless cancelled first.
 *
 * @param {() => Promise<any>} run the actual send call
 * @param {{ delayMs?: number, snapshot?: { draftKey:string, to?:string, subject?:string, body?:string } }} [opts]
 *   `snapshot` is persisted for the life of the window so a crashed tab can hand
 *   the text back (TICKET-858). It is dropped the moment the send resolves — the
 *   caller decides, via the hook, whether the text also goes back to the draft.
 * @returns {{ id:number, promise:Promise<{status:'sent'|'failed'|'canceled', result?:any, error?:any}>,
 *             flush:() => void, cancel:() => boolean }}
 *   `cancel()` returns false when the window has already closed — the caller must
 *   tell the truth ("too late") rather than claim an undo that did not happen.
 */
// TICKET-1366 (P1, peer review 2026-10-19 #16): every composer queues the send and then
// closes its sheet, which stays mounted and clickable for 300 ms. A double tap queued two
// identical jobs and the customer got the quote email (with the PDF) twice. An identical
// email (same draft, recipient, subject and body) that is still inside its undo window is
// the SAME send: return that job, marked `deduped`, instead of queueing another.
const pendingBySignature = new Map();
function sendSignature(snapshot) {
  if (!snapshot || !snapshot.draftKey) return null;
  return JSON.stringify([snapshot.draftKey, snapshot.to ?? '', snapshot.subject ?? '', snapshot.body ?? '']);
}

export function queueSend(run, { delayMs = UNDO_WINDOW_MS, snapshot = null } = {}) {
  const sig = sendSignature(snapshot);
  const existing = sig ? pendingBySignature.get(sig) : null;
  if (existing && pending.has(existing.id) && snapshotLive(existing.id)) return { ...existing, deduped: true };
  const id = nextId++;
  let settle;
  const promise = new Promise((resolve) => { settle = resolve; });
  writeSnapshot(id, snapshot);

  const forget = () => { if (sig && pendingBySignature.get(sig)?.id === id) pendingBySignature.delete(sig); };
  const fire = () => {
    const entry = pending.get(id);
    if (!entry) return; // already fired or cancelled
    clearTimeout(entry.timer);
    pending.delete(id);
    forget();
    Promise.resolve()
      .then(run)
      .then(
        (result) => { dropSnapshot(id); settle({ status: 'sent', result }); },
        (error) => { dropSnapshot(id); settle({ status: 'failed', error }); },
      );
  };

  const timer = setTimeout(fire, delayMs);
  pending.set(id, { timer, fire });

  const job = {
    id,
    snapshot,
    promise,
    flush: fire,
    cancel() {
      const entry = pending.get(id);
      if (!entry) return false;
      clearTimeout(entry.timer);
      pending.delete(id);
      forget();
      dropSnapshot(id);
      settle({ status: 'canceled' });
      return true;
    },
  };
  if (sig) pendingBySignature.set(sig, job);
  return job;
}

/** Send everything still waiting, now. */
export function flushPendingSends() {
  for (const entry of [...pending.values()]) entry.fire();
}

/** Test seam + "is anything still held?" for callers that want to warn. */
export function pendingSendCount() {
  return pending.size;
}

// Leaving the page must not swallow a queued email: flush on the way out.
// `pagehide` fires when the page is actually being torn down (closing the tab).
// We do NOT flush on `visibilitychange` to 'hidden' (backgrounding the PWA):
// on iPhone, backgrounding is frequent (lock, app-switch), so flushing would
// collapse the 30s undo window to ~0s. Instead, queued emails are persisted to
// localStorage (TICKET-858) and recovered as drafts if the app crashes or closes.
// This is safer: crash + recover as draft (never duplicates) beats crash + resend
// (might duplicate). The CEO gets data safety over speed.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPendingSends);
}
